<template>
  <main class="project-detail-page">
    <!-- Per-page SEO: unique title + meta description (Requirement 10.1).
         The metadata is derived from the loaded project so each case study
         gets a distinct, descriptive title/description. -->
    <SeoHead :seo="seo" />

    <!-- Loading state -->
    <section
      v-if="status === 'loading'"
      class="project-detail__status"
      aria-labelledby="project-detail-loading-heading"
      aria-busy="true"
    >
      <v-container>
        <div class="project-detail__status-inner">
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
            class="mb-4"
          />
          <h1 id="project-detail-loading-heading" class="project-detail__status-heading">
            Loading project…
          </h1>
        </div>
      </v-container>
    </section>

    <!-- Error state (Requirement 6.6): show an error message and no stale data. -->
    <section
      v-else-if="status === 'error'"
      class="project-detail__status"
      aria-labelledby="project-detail-error-heading"
    >
      <v-container>
        <div class="project-detail__status-inner">
          <h1 id="project-detail-error-heading" class="project-detail__status-heading">
            We couldn’t load this project
          </h1>
          <p class="project-detail__status-body">
            Something went wrong while fetching this project. Please try again,
            or head back to browse all of our work.
          </p>
          <div class="project-detail__status-actions">
            <CtaButton label="Try again" @click="loadProject" />
            <CtaButton
              to="/projects"
              label="Back to projects"
              variant="outlined"
            />
          </div>
        </div>
      </v-container>
    </section>

    <!-- Not-found state: the id did not match any website project. -->
    <section
      v-else-if="status === 'not-found'"
      class="project-detail__status"
      aria-labelledby="project-detail-missing-heading"
    >
      <v-container>
        <div class="project-detail__status-inner">
          <h1 id="project-detail-missing-heading" class="project-detail__status-heading">
            Project not found
          </h1>
          <p class="project-detail__status-body">
            We couldn’t find the project you’re looking for. It may have been
            moved or is no longer published. Browse our other work instead.
          </p>
          <CtaButton to="/projects" label="Back to projects" />
        </div>
      </v-container>
    </section>

    <!-- Ready state: the project case study (Requirement 6.4). -->
    <template v-else-if="status === 'ready' && project">
      <!-- Hero header with title, category, and image. -->
      <section class="project-detail__hero" aria-labelledby="project-detail-heading">
        <v-container>
          <ScrollReveal>
            <div class="project-detail__category text-uppercase">
              {{ project.serviceCategory }}
            </div>
            <h1 id="project-detail-heading" class="project-detail__heading">
              {{ project.title }}
            </h1>
            <p v-if="project.summary" class="project-detail__summary">
              {{ project.summary }}
            </p>
          </ScrollReveal>
        </v-container>
      </section>

      <!-- Project imagery; branded placeholder when no image is available. -->
      <section class="project-detail__media" aria-label="Project image">
        <v-container>
          <ScrollReveal>
            <v-img
              v-if="project.imageUrl"
              :src="project.imageUrl"
              :alt="project.imageAlt"
              :aspect-ratio="16 / 9"
              cover
              class="project-detail__image"
              loading="lazy"
            />
            <div
              v-else
              class="project-detail__placeholder"
              role="img"
              :aria-label="project.imageAlt"
            >
              <svg
                class="project-detail__placeholder-glyph"
                viewBox="0 0 64 64"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  fill="currentColor"
                  d="M8 12h48a2 2 0 0 1 2 2v36a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V14a2 2 0 0 1 2-2Zm2 4v25.2l11.5-11.5a2 2 0 0 1 2.8 0L33 38.4l9.7-9.7a2 2 0 0 1 2.8 0L54 37.2V16H10Zm34-2.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Z"
                />
              </svg>
            </div>
          </ScrollReveal>
        </v-container>
      </section>

      <!-- Case-study body. -->
      <section
        class="project-detail__body"
        aria-labelledby="project-detail-body-heading"
      >
        <v-container>
          <ScrollReveal>
            <h2 id="project-detail-body-heading" class="project-detail__body-heading">
              About this project
            </h2>
            <p
              v-if="project.description"
              class="project-detail__body-text"
            >
              {{ project.description }}
            </p>
            <p v-else class="project-detail__body-text project-detail__body-text--muted">
              More details about this project are coming soon. Get in touch to
              learn how we delivered work like this.
            </p>
          </ScrollReveal>
        </v-container>
      </section>

      <!-- Closing CTAs: back to projects and contact (conversion goal). -->
      <section class="project-detail__cta" aria-labelledby="project-detail-cta-heading">
        <v-container>
          <ScrollReveal>
            <div class="project-detail__cta-inner">
              <h2 id="project-detail-cta-heading" class="project-detail__cta-heading">
                Have a project like this in mind?
              </h2>
              <p class="project-detail__cta-body">
                Talk to our team about how ThinkSpatial can support your next
                project.
              </p>
              <div class="project-detail__cta-actions">
                <CtaButton to="/contact" label="Get in touch" />
                <CtaButton
                  to="/projects"
                  label="Back to projects"
                  variant="outlined"
                />
              </div>
            </div>
          </ScrollReveal>
        </v-container>
      </section>
    </template>
  </main>
</template>

<script>
// ProjectDetailPage — single project case-study view (Requirement 6.4).
//
// Loads one project by the `id` route parameter via
// `ProjectsApi.getProject(id)`, normalizes the raw payload with
// `normalizeProject` so missing/malformed fields render gracefully
// (Requirement 11.4), and tracks loading / ready / error / not-found states so
// each is handled explicitly. A failed request never shows partial or stale
// data — the project is cleared before each load (Requirement 6.6).
//
// SEO metadata (SeoHead/useSeo) is derived from the loaded project for a unique
// per-page title and description (Requirement 10.1), and CtaButtons offer a
// back-to-projects path and a contact conversion CTA.
//
// Uses the Options API to match the project's `plugin:vue/essential` ESLint
// preset (no `<script setup>`).
import ProjectsApi from '@/services/ProjectsApi'
import { normalizeProject } from '@/services/projectNormalizer'
import SeoHead from '@/components/SeoHead.vue'
import ScrollReveal from '@/components/ScrollReveal.vue'
import CtaButton from '@/components/CtaButton.vue'

export default {
  name: 'ProjectDetailPage',
  components: {
    SeoHead,
    ScrollReveal,
    CtaButton,
  },
  data () {
    return {
      // Lifecycle state: 'loading' | 'ready' | 'error' | 'not-found'.
      status: 'loading',
      // Normalized Project view model, or null until loaded/if not found.
      project: null,
    }
  },
  computed: {
    // The project id from the route param (Requirement 6.4).
    projectId () {
      return this.$route.params.id
    },
    // Per-page SEO derived from the loaded project, with sensible fallbacks
    // for the non-ready states (Requirement 10.1).
    seo () {
      if (this.status === 'ready' && this.project) {
        const summary = this.project.summary || this.project.description
        return {
          title: `${this.project.title} | ThinkSpatial`,
          description: summary
            ? summary.slice(0, 200)
            : `${this.project.title} — a ${this.project.serviceCategory} project delivered by ThinkSpatial.`,
        }
      }
      if (this.status === 'not-found') {
        return {
          title: 'Project not found | ThinkSpatial',
          description:
            'The project you requested could not be found. Browse all '
            + 'ThinkSpatial projects to see examples of our spatial work.',
        }
      }
      return {
        title: 'Project | ThinkSpatial',
        description:
          'Explore a ThinkSpatial project case study showcasing our '
          + 'surveying, GIS, UAV, and spatial technology work.',
      }
    },
  },
  watch: {
    // Reload when navigating between project detail pages (param change),
    // since the component instance is reused for the same route.
    projectId () {
      this.loadProject()
    },
  },
  created () {
    this.loadProject()
  },
  methods: {
    // Fetch and normalize the project for the current route id. Clears any
    // previously loaded project before fetching so a failed/empty load can
    // never leave stale data on screen (Requirement 6.6).
    loadProject () {
      const id = this.projectId
      this.status = 'loading'
      this.project = null

      if (id === undefined || id === null || String(id).trim() === '') {
        this.status = 'not-found'
        return
      }

      ProjectsApi.getProject(id)
        .then((response) => {
          const raw = response && response.data && response.data.project
          if (!raw) {
            this.status = 'not-found'
            return
          }
          this.project = normalizeProject(raw)
          this.status = 'ready'
        })
        .catch(() => {
          // Network/transport failure: surface the error state with no data.
          this.project = null
          this.status = 'error'
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.project-detail__status {
  padding: 120px 0;
}

.project-detail__status-inner {
  max-width: 640px;
  margin: 0 auto;
  text-align: center;
}

.project-detail__status-heading {
  font-size: 2.25rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 16px;
}

.project-detail__status-body {
  font-size: 1.1rem;
  color: #4a4a4a;
  line-height: 1.7;
  margin-bottom: 32px;
}

.project-detail__status-actions,
.project-detail__cta-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
}

.project-detail__hero {
  padding: 64px 0 32px;
}

.project-detail__category {
  color: #e7511e; /* brand red-orange accent */
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 8px;
}

.project-detail__heading {
  font-size: 2.75rem;
  font-weight: 800;
  line-height: 1.15;
  color: #41281b;
  margin-bottom: 16px;
}

.project-detail__summary {
  font-size: 1.2rem;
  font-weight: 600;
  color: #4a4a4a;
  line-height: 1.6;
  max-width: 760px;
}

.project-detail__media {
  padding: 16px 0;
}

.project-detail__image {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
}

// On desktop the image otherwise spans the full v-container width, which reads
// as oversized next to the narrower measure of the body text beneath it.
// Capped from tablet up and left-aligned so its edge lines up with the heading
// and body text below, which are plain flush-left block content. Mobile is
// intentionally left at 100% (unchanged) since it already reads well full-width
// on a small screen.
@media (min-width: 601px) {
  .project-detail__image {
    max-width: 720px;
  }
}

.project-detail__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  background: linear-gradient(135deg, #41281b 0%, #5a3826 100%);
  color: rgba(242, 148, 0, 0.85); /* brand burnt orange */
}

.project-detail__placeholder-glyph {
  width: 18%;
  max-width: 120px;
  height: auto;
}

.project-detail__body {
  padding: 48px 0;
}

.project-detail__body-heading {
  font-size: 2rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 16px;
}

.project-detail__body-text {
  font-size: 1.05rem;
  color: #4a4a4a;
  line-height: 1.7;
  max-width: 760px;
  white-space: pre-line;
}

.project-detail__body-text--muted {
  color: #929292;
}

.project-detail__cta {
  padding: 64px 0;
  background-color: #41281b;
}

.project-detail__cta-inner {
  text-align: center;
}

.project-detail__cta-heading {
  font-size: 2rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 16px;
}

.project-detail__cta-body {
  font-size: 1.1rem;
  color: #e6ded9;
  margin-bottom: 24px;
}

@media (max-width: 600px) {
  .project-detail__heading {
    font-size: 2rem;
  }

  .project-detail__status {
    padding: 72px 0;
  }
}
</style>
